import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SkeletonLoader from "../../../components/SkeletonLoader";
import BackendRfiGridTable from "../../../components/RFIs/BackendRfiGridTable";
import Pagination from "../../../components/CustomPagination";
import Notification from "../../../components/Notification";
import {
  useGetListForSponsorFilters,
  useGetMyRfisAdmin,
  useGetprovidersListForFilters,
  useListOfQuestionnaireTemplateForAdmin,
} from "../../../custom-hooks/queries";
import { useGridExportRegistration } from "../../../custom-hooks/useGridExportRegistration";
import useRequestListStickyFilters from "../../../custom-hooks/useRequestListStickyFilters";
import { mapQuestionnaireTemplateToFilterOption } from "../../../utils/catalogFilterOptions";
import { setSearchTextByPage } from "../../../redux-store/features/providerFiltersSlice";
import PageToolbarFilters from "../../../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../../../components/Filters/ToolbarSearch";
import TableExportMenu from "../../../components/Menus/TableExportMenu";
import { rfiCellStyles } from "../../../utils/exportBadgeStyles";
import {
  buildBackendRfisExportData,
  BACKEND_RFIS_USE_SERVER_SORT,
  sortBackendRfiConfigToApiSortParams,
  sortBackendRfiItems,
  filterMyRfiItemsByClientFilters,
} from "./backendRfisGridMapper";
import { REQUEST_LIST_SCREEN_KEYS } from "../../../utils/requestListStickyFilterPersistence";
import { resolveRfiRowDetailsPath } from "../../../utils/gridOrgNameLinks";

const BACKEND_RFIS_PAGE_KEY = "backendRfis";

const MY_RFI_TYPE_FILTER_OPTIONS = [
  { value: "diligent", label: "Diligent VQQ" },
  { value: "custom", label: "Custom VQQ" },
];

/** Default Status filter: all except Archived and Cancelled (admins opt in). */
const BACKEND_RFI_STATUS_IDS_DEFAULT = [
  "Sent",
  "In Progress",
  "In Review",
  "In Remediation (Vendor)",
  "In Remediation (Purchaser)",
  "Complete",
];

const ADMIN_VQQ_EMPTY_SELECTED_IDS = {
  sponsor: [],
  provider: [],
  status: [...BACKEND_RFI_STATUS_IDS_DEFAULT],
  templates: [],
  services: [],
  type: [],
  gxp: [],
};

const ADMIN_VQQ_DEFAULT_SORT = {
  requestDate: { direction: "desc" },
};

export const BackendRfis = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  const { data: sponsors } = useGetListForSponsorFilters();
  const { data: providers } = useGetprovidersListForFilters();
  const { data: questionnares } = useListOfQuestionnaireTemplateForAdmin();

  const templatesFilterOptions = useMemo(() => {
    const seen = new Set();
    return (questionnares || [])
      .map(mapQuestionnaireTemplateToFilterOption)
      .filter((opt) => {
        const key = opt.label || opt.name;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) =>
        (a.label || a.name || "").localeCompare(b.label || b.name || "")
      );
  }, [questionnares]);

  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage[BACKEND_RFIS_PAGE_KEY]
    ) || "";

  const {
    selectedIds,
    setSelectedIds,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    sortFetchNonce,
    dueDateFilter,
    setDueDateFilter,
  } = useRequestListStickyFilters({
    screenKey: REQUEST_LIST_SCREEN_KEYS.ADMIN_VQQS,
    emptySelectedIds: ADMIN_VQQ_EMPTY_SELECTED_IDS,
    defaultSortConfig: ADMIN_VQQ_DEFAULT_SORT,
    searchQuery,
    includeDueDate: true,
    useServerSort: BACKEND_RFIS_USE_SERVER_SORT,
  });

  const filters = useMemo(
    () => [
      {
        id: "sponsor",
        name: "Sponsor",
        searchable: true,
        options: sponsors || [],
        total: sponsors?.length || 0,
      },
      {
        id: "provider",
        name: "Vendor",
        searchable: true,
        options: providers || [],
        total: 1335,
      },
      {
        id: "type",
        name: "Type",
        searchable: false,
        options: MY_RFI_TYPE_FILTER_OPTIONS,
        total: MY_RFI_TYPE_FILTER_OPTIONS.length,
      },
      // TODO: uncomment for enable GXP filter (import MY_RFI_GXP_FILTER_OPTIONS from myRfiGridRowMapper)
      // {
      //   id: "gxp",
      //   name: "GxP",
      //   searchable: true,
      //   options: MY_RFI_GXP_FILTER_OPTIONS,
      //   total: MY_RFI_GXP_FILTER_OPTIONS.length,
      // },
      {
        id: "templates",
        name: "Templates",
        searchable: true,
        options: templatesFilterOptions,
        total: templatesFilterOptions?.length || 0,
      },
      {
        id: "status",
        name: "Status",
        searchable: false,
        options: [
          { statusID: "Sent", label: "Sent", checked: false },
          { statusID: "In Progress", label: "In Progress", checked: false },
          { statusID: "In Review", label: "In Review", checked: false },
          {
            statusID: "In Remediation (Vendor)",
            label: "In Remediation (Vendor)",
            checked: false,
          },
          {
            statusID: "In Remediation (Purchaser)",
            label: "In Remediation (Purchaser)",
            checked: false,
          },
          { statusID: "Complete", label: "Complete", checked: false },
          { statusID: "Cancelled", label: "Cancelled", checked: false },
          { statusID: "Archived", label: "Archived", checked: false },
        ],
        total: 8,
      },
      {
        id: "dueDate",
        name: "Due Date",
        type: "date",
        value: dueDateFilter,
        onChange: setDueDateFilter,
      },
    ],
    [sponsors, providers, templatesFilterOptions, dueDateFilter]
  );

  const serverSortParams = useMemo(() => {
    if (!BACKEND_RFIS_USE_SERVER_SORT) return undefined;
    return sortBackendRfiConfigToApiSortParams(sortConfig);
  }, [sortConfig]);

  const {
    data: rfisData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetMyRfisAdmin(
    currentPage,
    searchQuery,
    selectedIds.provider,
    selectedIds.templates,
    selectedIds.status,
    selectedIds.services,
    selectedIds.sponsor,
    serverSortParams,
    sortFetchNonce,
    dueDateFilter
  );

  const sortedItems = useMemo(() => {
    const items = rfisData?.items || [];
    const afterLocal = filterMyRfiItemsByClientFilters(items, selectedIds);
    if (BACKEND_RFIS_USE_SERVER_SORT) {
      return afterLocal;
    }
    return sortBackendRfiItems(afterLocal, sortConfig);
  }, [rfisData?.items, selectedIds, sortConfig]);

  const exportDataRef = useRef(null);
  const handleExportDataReady = useCallback((fn) => {
    exportDataRef.current = fn;
  }, []);

  useGridExportRegistration(
    handleExportDataReady,
    () =>
      buildBackendRfisExportData({
        searchQuery,
        selectedIds,
        serverSortParams,
        sortConfig,
        dueDateFilter,
      }),
    [searchQuery, selectedIds, serverSortParams, sortConfig, dueDateFilter, handleExportDataReady]
  );

  const handleRowClick = useCallback(
    (item) => {
      const path = resolveRfiRowDetailsPath(item);
      if (path) navigate(path);
    },
    [navigate]
  );

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="bg-white py-4 flex flex-col gap-4 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="sm:flex-auto min-w-0">
            <h1 className="text-lg font-medium text-gray-900">VQQ Requests</h1>
            <p className="mt-1 text-sm text-gray-500">
              The below table displays all active and historical Vendor Qualification Questionnaire (VQQ) requests.
            </p>
          </div>
          <div className="flex shrink-0 justify-end mt-4">
            <TableExportMenu
              getData={() =>
                exportDataRef.current
                  ? exportDataRef.current()
                  : { records: [], headers: [] }
              }
              reportTitle="VQQ Requests (Admin)"
              reportSubtitle="All active and historical VQQ requests"
              filePrefix="VQQ_Requests_Admin"
              downloadType="VQQ Requests"
              cellStyles={rfiCellStyles}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 w-full min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full min-w-0">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-3 lg:max-w-xl">
            <ToolbarSearch
              value={searchQuery}
              onDebouncedChange={(text) =>
                dispatch(
                  setSearchTextByPage({
                    pageKey: BACKEND_RFIS_PAGE_KEY,
                    searchText: text,
                  })
                )
              }
              placeholder="Search your VQQ request details..."
            />
          </div>
          <div className="w-full min-w-0 lg:w-auto lg:flex-shrink-0">
            <PageToolbarFilters
              sections={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
            />
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-4 py-6 sm:px-6">
            <SkeletonLoader />
          </div>
        ) : (
          <BackendRfiGridTable
            data={sortedItems}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={handleRowClick}
            refetch={refetch}
            isSearchActive={!!searchQuery}
          />
        )}

        {!isError &&
          !isLoading &&
          sortedItems &&
          sortedItems.length > 0 && (
            <div className="border-t border-gray-200">
              <Pagination
                totalPages={rfisData?.total_pages || 1}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
      </div>

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error && error.message}
        status="error"
      />
    </div>
  );
};
